import { ASSISTANTS_QUERY_KEY } from "@frontend-shared/constants";
import { EAssistantType, EProjectPermission } from "@frontend-shared/types";
import { useQuery } from "@tanstack/react-query";

import { assistantsApi } from "@/api/services";
import { usePermissionCheck } from "@/hooks/use-permission";

export interface IUseAssistantsProps {
  type?: EAssistantType;
  // NOTE: NSAI-5678 — ANDed with the permission check below, not a replacement
  // for it. Lets a caller that only needs the list situationally (e.g. the
  // sidebar fetching avatars only once it has assistant chats to show) skip
  // the request rather than pull the whole list, avatars included, on every
  // mount regardless of whether it has anything to render.
  enabled?: boolean;
}

export const useAssistants = ({
  type = EAssistantType.ALL,
  enabled = true,
}: IUseAssistantsProps = {}) => {
  // NOTE: Either private or public assistant read is sufficient — the server's
  // GET /assistants narrows the result set per the caller's actual flags.
  const [hasPrivateAssistantReadPermission, hasPublicAssistantReadPermission] =
    usePermissionCheck([
      EProjectPermission.PRIVATE_ASSISTANT_READ,
      EProjectPermission.PUBLIC_ASSISTANT_READ,
    ]);

  return useQuery({
    queryKey: [ASSISTANTS_QUERY_KEY, type],
    queryFn: () => assistantsApi.getAssistants(type),
    enabled:
      enabled &&
      (hasPrivateAssistantReadPermission || hasPublicAssistantReadPermission),
  });
};

export const useAssistant = (uuid: string) => {
  return useQuery({
    queryKey: [ASSISTANTS_QUERY_KEY, uuid],
    queryFn: () => assistantsApi.getByUuid(uuid),
    enabled: !!uuid,
  });
};
